import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, PhoneOff, Send, Volume2, VolumeX } from 'lucide-react';
import {
  mockActionResult,
  mockStafflyAssistant,
  PendingAction,
} from './aiAssistant/mockStafflyAssistant';
import { useBrowserVoice, VoiceMode } from './aiAssistant/useBrowserVoice';
import { RealtimeFunctionCall, useOpenAiRealtime } from './aiAssistant/useOpenAiRealtime';
import { aiAssistantApi, AiAssistantStatus } from '../services/aiAssistantApi';

type Place = 'office' | 'site';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  pendingAction?: PendingAction;
}

const TIPS = [
  'Who is absent today?',
  'Whose visa is expiring?',
  'Approve Jane’s holiday',
  'Create a shift for Alex tomorrow',
];

const AiAssistant: React.FC = () => {
  const [place, setPlace] = useState<Place>('office');
  const [mode, setMode] = useState<VoiceMode>('tap');
  const [ttsOn, setTtsOn] = useState(true);
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: 'welcome',
      role: 'assistant',
      text: 'Text chat and Realtime Voice sample. With an OpenAI key on Staffly.API, typed messages use Chat Completions and the mic uses Realtime. Without a key, this page stays on the local mock.',
    },
  ]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<AiAssistantStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const holdingRef = useRef(false);
  const ttsOnRef = useRef(ttsOn);
  ttsOnRef.current = ttsOn;

  const openaiReady = !!status?.configured;
  const silenceMs = place === 'site' ? 1800 : 1400;

  const handleUtterance = useCallback((text: string) => {
    setDraft(text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(text.length, text.length);
    });
  }, []);

  const browserVoice = useBrowserVoice({
    mode,
    silenceMs,
    onUtterance: handleUtterance,
  });

  const appendUser = useCallback((text: string) => {
    setMessages((prev) => {
      if (prev[prev.length - 1]?.role === 'user' && prev[prev.length - 1]?.text === text) return prev;
      return [...prev, { id: `u-${Date.now()}`, role: 'user', text }];
    });
  }, []);

  const speakRef = useRef(browserVoice.speak);
  speakRef.current = browserVoice.speak;

  const appendAssistant = useCallback((text: string, pendingAction?: PendingAction, speakText?: string) => {
    setMessages((prev) => [
      ...prev,
      { id: `a-${Date.now()}`, role: 'assistant', text, pendingAction },
    ]);
    if (ttsOnRef.current && speakText && !openaiReady) {
      speakRef.current(speakText);
    }
  }, [openaiReady]);

  const handleRealtimeToolRef = useRef<(call: RealtimeFunctionCall) => void>(() => undefined);

  const realtime = useOpenAiRealtime({
    onUserTranscript: (text) => appendUser(text),
    onAssistantTranscript: (text, done) => {
      if (done) {
        setStreaming('');
        appendAssistant(text);
      } else {
        setStreaming(text);
      }
    },
    onFunctionCall: (call) => handleRealtimeToolRef.current(call),
  });

  handleRealtimeToolRef.current = (call) => {
    void (async () => {
      try {
        const result = await aiAssistantApi.executeTool(call.name, call.arguments);
        realtime.sendFunctionOutput(call.callId, result);
        const pending = result.pendingAction as PendingAction | undefined;
        if (pending) {
          appendAssistant('I need you to confirm this on screen before I do it.', pending);
        }
      } catch (e) {
        realtime.sendFunctionOutput(call.callId, {
          error: e instanceof Error ? e.message : 'Tool failed',
        });
      }
    })();
  };

  useEffect(() => {
    let cancelled = false;
    aiAssistantApi
      .status()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) {
          setStatus({ configured: false, chatModel: '', realtimeModel: '' });
          setStatusError('Could not reach the AI Assistant API. Using the local mock.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, browserVoice.liveText, busy, streaming, realtime.micEnabled]);

  const applyPlace = (next: Place) => {
    setPlace(next);
    if (next === 'office') {
      setMode('tap');
      setTtsOn(true);
    } else {
      setMode('hold');
      setTtsOn(true);
    }
  };

  const sendText = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy || browserVoice.listening) return;
    browserVoice.cancelSpeak();
    setDraft('');
    appendUser(text);
    setBusy(true);
    try {
      if (openaiReady) {
        const history = [...messages, { id: '', role: 'user' as const, text }]
          .filter((m) => m.id !== 'welcome')
          .map((m) => ({ role: m.role, content: m.text }));
        const result = await aiAssistantApi.chat(history);
        appendAssistant(result.text, result.pendingAction);
      } else {
        await new Promise((r) => setTimeout(r, 280));
        const turn = mockStafflyAssistant(text);
        appendAssistant(turn.text, turn.pendingAction, turn.speak);
      }
    } catch (e) {
      const turn = mockStafflyAssistant(text);
      appendAssistant(
        `${turn.text}\n\n(OpenAI chat failed: ${e instanceof Error ? e.message : 'error'}. Showing local mock.)`,
        turn.pendingAction,
        turn.speak,
      );
    } finally {
      setBusy(false);
    }
  };

  const resolveAction = async (action: PendingAction, approved: boolean) => {
    setMessages((prev) =>
      prev.map((m) => (m.pendingAction === action ? { ...m, pendingAction: undefined } : m)),
    );
    if (openaiReady) {
      try {
        const result = await aiAssistantApi.executeTool(
          'confirm_write_action',
          { kind: action.kind, approved },
          approved,
        );
        const text = String(result.text ?? (approved ? 'Done.' : 'Cancelled.'));
        appendAssistant(text);
        if (realtime.connected) {
          realtime.sendUserText(
            approved
              ? `The user tapped Confirm for ${action.kind}.`
              : `The user tapped Cancel for ${action.kind}. Do not perform it.`,
          );
        }
        return;
      } catch {
        /* fall through to mock */
      }
    }
    const turn = mockActionResult(action.kind, approved);
    appendAssistant(turn.text, undefined, turn.speak);
  };

  const ensureRealtime = async () => {
    if (!openaiReady) return false;
    if (!realtime.connected && !realtime.connecting) {
      await realtime.connect();
    }
    return true;
  };

  const onHoldPointerDown = async (event: React.PointerEvent) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    if (openaiReady) {
      holdingRef.current = true;
      await ensureRealtime();
      if (holdingRef.current) realtime.setMicEnabled(true);
      return;
    }
    browserVoice.onHoldPointerDown(event);
  };

  const onHoldPointerUp = (event: React.PointerEvent) => {
    event.preventDefault();
    holdingRef.current = false;
    if (openaiReady) {
      realtime.setMicEnabled(false);
      return;
    }
    browserVoice.onHoldPointerUp(event);
  };

  const onTapMic = async () => {
    if (openaiReady) {
      if (!realtime.connected) {
        await realtime.connect();
        realtime.setMicEnabled(true);
        return;
      }
      realtime.setMicEnabled(!realtime.micEnabled);
      return;
    }
    browserVoice.startTap();
  };

  const listening = openaiReady ? realtime.micEnabled : browserVoice.listening;
  const voiceError = openaiReady ? realtime.error : browserVoice.error;

  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendText(draft);
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[32rem] flex-col bg-neutral-100">
      <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col bg-white shadow-sm lg:my-3 lg:rounded-xl lg:border lg:border-neutral-200">
        <header className="flex-shrink-0 border-b border-neutral-200 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-lg font-semibold text-neutral-900">AI Assistant</h1>
              <p className="mt-0.5 text-xs text-neutral-500">
                {openaiReady
                  ? `OpenAI text + Realtime · ${status?.chatModel} / ${status?.realtimeModel}`
                  : 'Local mock · set OpenAI:ApiKey on Staffly.API for live text + voice'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  openaiReady ? 'bg-emerald-50 text-emerald-800' : 'bg-neutral-100 text-neutral-600'
                }`}
              >
                {openaiReady ? 'OpenAI' : 'Mock'}
              </span>
              {openaiReady && realtime.connected && (
                <button
                  type="button"
                  onClick={realtime.disconnect}
                  className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800"
                >
                  <PhoneOff className="h-3.5 w-3.5" />
                  End voice
                </button>
              )}
              {!openaiReady && (
                <button
                  type="button"
                  onClick={() => {
                    browserVoice.cancelSpeak();
                    setTtsOn((v) => !v);
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
                    ttsOn
                      ? 'border-blue-200 bg-blue-50 text-blue-800'
                      : 'border-neutral-200 bg-neutral-50 text-neutral-600'
                  }`}
                >
                  {ttsOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                  {ttsOn ? 'Speak replies' : 'Muted'}
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                Place
              </span>
              <div className="flex rounded-lg border border-neutral-200 p-0.5">
                {(['office', 'site'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => applyPlace(p)}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${
                      place === p ? 'bg-blue-600 text-white' : 'text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {p === 'office' ? 'Office' : 'On-site'}
                  </button>
                ))}
              </div>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                Mic
              </span>
              <div className="flex rounded-lg border border-neutral-200 p-0.5">
                {([
                  ['tap', 'Tap to talk'],
                  ['hold', 'Hold to talk'],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    disabled={listening}
                    onClick={() => setMode(value)}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium disabled:opacity-50 ${
                      mode === value ? 'bg-blue-600 text-white' : 'text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </label>
          </div>

          {statusError && (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">{statusError}</p>
          )}
          {!openaiReady && !browserVoice.supported && (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              This browser cannot do speech-to-text. Open Staffly in Chrome or Edge, or type below.
            </p>
          )}
          {voiceError && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{voiceError}</p>
          )}
          {realtime.connecting && (
            <p className="mt-2 text-xs text-neutral-500">Starting OpenAI Realtime…</p>
          )}
        </header>

        <div ref={threadRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed sm:max-w-[80%] ${
                  m.role === 'user'
                    ? 'rounded-br-md bg-blue-600 text-white'
                    : 'rounded-bl-md bg-neutral-100 text-neutral-800'
                }`}
              >
                <p className="whitespace-pre-wrap">{m.text}</p>
                {m.pendingAction && (
                  <div className="mt-3 overflow-hidden rounded-xl border border-amber-200 bg-white text-neutral-800">
                    <div className="bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-950">
                      {m.pendingAction.title}
                    </div>
                    <dl className="space-y-1.5 px-3 py-2 text-sm">
                      {m.pendingAction.details.map((row) => (
                        <div key={row.label} className="flex justify-between gap-3">
                          <dt className="text-neutral-500">{row.label}</dt>
                          <dd className="text-right font-medium">{row.value}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="px-3 pb-2 text-xs text-neutral-500">{m.pendingAction.summary}</p>
                    <div className="grid grid-cols-2 gap-2 border-t border-neutral-100 p-2">
                      <button
                        type="button"
                        onClick={() => void resolveAction(m.pendingAction!, false)}
                        className="rounded-lg border border-neutral-200 px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => void resolveAction(m.pendingAction!, true)}
                        className="rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {(listening || streaming) && (
            <div className="flex justify-start">
              <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-blue-50 px-3.5 py-2.5 text-sm text-blue-900 sm:max-w-[80%]">
                <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  </span>
                  {streaming
                    ? 'Speaking'
                    : openaiReady
                      ? (mode === 'hold' ? 'Realtime — release to pause' : 'Realtime — tap to pause')
                      : (mode === 'hold' ? 'Holding — release to stop' : 'Listening — pauses to finish')}
                </div>
                <p className="whitespace-pre-wrap">
                  {streaming || browserVoice.liveText || (openaiReady ? 'Listening…' : '…')}
                </p>
                {!openaiReady && (
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-blue-100">
                    <div
                      className="h-full rounded-full bg-blue-600 transition-[width] duration-75"
                      style={{ width: `${Math.max(6, Math.round(browserVoice.level * 100))}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {busy && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md bg-neutral-100 px-3.5 py-2.5 text-sm text-neutral-500">
                Thinking…
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-neutral-200 px-3 py-3">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {TIPS.map((tip) => (
              <button
                key={tip}
                type="button"
                onClick={() => setDraft(tip)}
                className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] text-neutral-600 hover:bg-white"
              >
                {tip}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-2">
            {mode === 'hold' ? (
              <button
                type="button"
                disabled={busy || realtime.connecting}
                onPointerDown={(e) => void onHoldPointerDown(e)}
                onPointerUp={onHoldPointerUp}
                onPointerCancel={onHoldPointerUp}
                onContextMenu={(e) => e.preventDefault()}
                className={`flex h-12 w-12 flex-shrink-0 select-none items-center justify-center rounded-full text-white touch-none ${
                  listening ? 'bg-red-600' : 'bg-blue-600 hover:bg-blue-700'
                } disabled:bg-neutral-300`}
                aria-label="Hold to talk"
              >
                <Mic className="h-5 w-5 pointer-events-none" />
              </button>
            ) : (
              <button
                type="button"
                disabled={busy || realtime.connecting}
                onClick={() => void onTapMic()}
                className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-white ${
                  listening ? 'bg-red-600' : 'bg-blue-600 hover:bg-blue-700'
                } disabled:bg-neutral-300`}
                aria-label={listening ? 'Stop listening' : 'Tap to talk'}
              >
                <Mic className="h-5 w-5" />
              </button>
            )}

            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onComposerKeyDown}
              placeholder={
                openaiReady
                  ? 'Type to OpenAI, or use the mic for Realtime Voice'
                  : (mode === 'hold' ? 'Hold the mic, then edit and send' : 'Tap the mic, then edit and send')
              }
              className="max-h-28 min-h-[3rem] flex-1 resize-none rounded-xl border border-neutral-200 px-3 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />

            <button
              type="button"
              disabled={!draft.trim() || busy || browserVoice.listening}
              onClick={() => void sendText(draft)}
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-300"
              aria-label="Send"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>

          <p className="mt-2 text-[11px] text-neutral-400">
            {openaiReady
              ? 'Send uses Chat Completions. Mic uses Realtime (key stays on the API). Writes still need Confirm.'
              : (mode === 'hold'
                ? 'Hold mic → captions → release → edit → Send.'
                : `Tap mic → talk → ${silenceMs / 1000}s silence → edit → Send.`)}
            {!openaiReady && browserVoice.firstPartialMs != null ? ` First caption ${browserVoice.firstPartialMs} ms.` : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AiAssistant;
